import classnames from 'classnames'
import { CuiButton, CuiIcon } from 'front-lib'
import { func, number, oneOfType, shape, string } from 'prop-types'
import { Fragment, PureComponent } from 'react'

import ShareIcon from '../../assets/icons/referral.svg'
import { CTA_WORDING } from '../../modules/Share/constants'
import Analytics from '../../utils/analytics'
import { getRatingText, isMaxRating, RATINGS } from '../../utils/rating'
import Chip from '../Chip'
import MealImage from '../MealImage'
import { TextArea } from '../TextArea/TextArea'

import './RateableItem.scss'

const ISSUES = [
  'Taste',
  'Ingredients',
  'Nutrition',
  'Shelf life',
  'Larger portion',
  'Smaller portion',
  'Packaging',
  'Delivery',
  'Other'
]

class RateableItem extends PureComponent {
  state = {
    active: false,
    completed: false,
    rating: 0,
    review: '',
    selectedIssues: []
  }

  onChange = e => {
    const { name, value } = e.target
    this.setState({ [name]: value })
  }

  onRate = rating => () => {
    if (isMaxRating(rating)) {
      this.setState({ active: true, rating, selectedIssues: [] })
    } else {
      this.setState({ active: true, rating })
    }
  }

  onReview = () => {
    const { rating, review, selectedIssues: reasons } = this.state
    const { onReview, id: productId, onEditReview, currentReview } = this.props
    this.setState({ active: false, completed: true })

    if (currentReview) {
      onEditReview({ id: currentReview.id, rating, review, reasons }, productId)
    } else {
      onReview({ productId, rating, review, reasons })
    }
  }

  onSelectIssue = issue => {
    const { selectedIssues } = this.state
    const newSelectedIssues = selectedIssues.filter(i => i !== issue)
    if (selectedIssues.length === newSelectedIssues.length) {
      newSelectedIssues.push(issue)
    }
    this.setState({ selectedIssues: newSelectedIssues })
  }

  currentRating = () => {
    const { rating } = this.state
    const { currentReview } = this.props

    if (rating) return rating
    if (currentReview) return currentReview.rating
    return null
  }

  getStarIconName = star => {
    const rating = this.currentRating()
    if (!rating) return 'star'
    return rating < star ? 'star' : 'starFull'
  }

  issueSelected = () => this.state.selectedIssues.length

  issueOtherSelected = () => this.state.selectedIssues.includes('Other')

  emptyReview = () => this.state.review === ''

  issueOtherSelectedAndEmptyReview = () =>
    this.issueOtherSelected() && this.emptyReview()

  disableReviewSubmission = () => {
    return !this.issueSelected() || this.issueOtherSelectedAndEmptyReview()
  }

  showIssuesError = () => !this.issueSelected()

  showReviewError = () => this.issueOtherSelectedAndEmptyReview()

  trackingReferral = () => {
    Analytics.track('Meals receiving a 5 star rating', {
      category: 'Share Cookunity',
      action: `Click on CTA to referral`
    })
  }

  render() {
    const { active, completed, review, rating, selectedIssues } = this.state
    const {
      className,
      name,
      description,
      picture,
      id: productId,
      currentReview,
      onToggleShareModal
    } = this.props

    const currentRating = this.currentRating()

    return (
      <>
        <div className={classnames('rateable-item', className)}>
          <MealImage
            mealImage={picture || '/no-image'}
            classNameNoImgContainer="no-image-container"
            classNameImage="meal_image"
            withoutText
          />
          <div className="meal_details">
            <p className="name">{name}</p>
            <p className="description">{description}</p>
            {(completed || currentReview) && (
              <div className="rating">
                {RATINGS.map(star => (
                  <div key={star} id={`meal-${productId}-${star}`}>
                    <CuiIcon
                      name={this.getStarIconName(star)}
                      className="star-icon disabled"
                    />
                  </div>
                ))}
              </div>
            )}
            {(completed || currentReview) && (
              <div className="rate_successful">
                <CuiIcon name="allDone" className="all-done-icon" />
                <p>Rated successfully!</p>
              </div>
            )}
            {!completed && !currentReview && (
              <>
                <div className="rating_wrapper">
                  {onToggleShareModal && (
                    <div className="rating_share">
                      <CuiButton
                        fill="outline"
                        onClick={() => onToggleShareModal(currentRating)}>
                        <span>{CTA_WORDING.RATE_MODAL.CTA_TEXT}</span>
                      </CuiButton>
                    </div>
                  )}
                  <p className="overall">
                    {currentReview && currentReview.review !== '-'
                      ? currentReview.review
                      : 'Overall'}
                  </p>
                  <div className="rating">
                    {RATINGS.map(star => (
                      <Fragment key={star}>
                        <div
                          key={star}
                          data-tooltip-id={`meal-${productId}-${star}`}
                          data-tooltip-place="top">
                          <CuiIcon
                            name={this.getStarIconName(star)}
                            className="star-icon"
                            onClick={this.onRate(star)}
                          />
                        </div>
                        {/* <Tooltip
                          id={`meal-${productId}-${star}`}
                          className="rating_tooltip">
                          {getRatingText(star)}
                        </Tooltip> */}
                      </Fragment>
                    ))}
                  </div>
                </div>
                <p className="rating_description">{getRatingText(rating)}</p>
              </>
            )}
            {!completed && !currentReview && active && (
              <div className="issues">
                {rating !== 5 && (
                  <p className="issues_label">
                    What can we improve to make it 5 stars?
                  </p>
                )}
                <div className="chips">
                  {ISSUES.map((issue, i) => (
                    <Chip
                      key={i}
                      className="chip"
                      label={issue}
                      onClick={() => this.onSelectIssue(issue)}
                      selected={selectedIssues.includes(issue)}
                    />
                  ))}
                </div>
                {this.showIssuesError() &&
                  (rating !== 5 ? (
                    <p className="error">Select 1 or more issues</p>
                  ) : (
                    <p className="error">Select 1 or more things you liked</p>
                  ))}
              </div>
            )}
            {!completed && !currentReview && active && (
              <div className="review">
                <TextArea
                  name="review"
                  value={review}
                  onChange={this.onChange}
                  placeholder={
                    rating === 5
                      ? 'What did you like the most?'
                      : 'Additional feedback'
                  }
                />
                {this.showReviewError() &&
                  (rating !== 5 ? (
                    <p className="error">Add an issue description here</p>
                  ) : (
                    <p className="error">Additional feedback</p>
                  ))}
                <CuiButton
                  disabled={this.disableReviewSubmission()}
                  onClick={this.onReview}>
                  Review
                </CuiButton>
              </div>
            )}
          </div>
        </div>
        {((completed && rating === 5) ||
          (currentReview && currentReview.rating === 5)) && (
          <div className="rate_successful_referral_row">
            <img src={ShareIcon} alt="checkmark" />
            <p>
              Glad you liked it! Tell a friend and you'll both get rewarded.
              <a
                onClick={() => this.trackingReferral()}
                target="_new"
                href="/referral"
                alt="share cookunity">
                Share CookUnity
              </a>
            </p>
          </div>
        )}
      </>
    )
  }
}

RateableItem.propTypes = {
  id: oneOfType([string, number]).isRequired,
  name: string.isRequired,
  onReview: func.isRequired,
  onEditReview: func.isRequired,
  description: string,
  picture: string,
  className: string,
  currentReview: shape({
    id: oneOfType([string, number]).isRequired,
    rating: oneOfType([string, number]),
    review: string
  }),
  onToggleShareModal: func
}

export default RateableItem
